import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Loader2, ArrowRight, ShieldCheck, Info, RefreshCw, Clock, XCircle, CheckCircle2, Upload } from "lucide-react";
import { toast } from "sonner";

export const CA_PROVINCES = [
  { code: "AB", name: "Alberta" },
  { code: "BC", name: "British Columbia" },
  { code: "MB", name: "Manitoba" },
  { code: "NB", name: "New Brunswick" },
  { code: "NL", name: "Newfoundland and Labrador" },
  { code: "NS", name: "Nova Scotia" },
  { code: "NT", name: "Northwest Territories" },
  { code: "NU", name: "Nunavut" },
  { code: "ON", name: "Ontario" },
  { code: "PE", name: "Prince Edward Island" },
  { code: "QC", name: "Quebec" },
  { code: "SK", name: "Saskatchewan" },
  { code: "YT", name: "Yukon" },
];

export const formatCaPostal = (v) => {
  const raw = (v || "").toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 6);
  if (/^\d/.test(raw)) return raw;
  return raw.length > 3 ? `${raw.slice(0, 3)} ${raw.slice(3)}` : raw;
};

export const validateCaForm = (form) => {
  const errors = {};
  if (!form.legal_business_name?.trim() || form.legal_business_name.trim().length < 2)
    errors.legal_business_name = "Legal business name is required";
  if (!form.street?.trim()) errors.street = "Street address is required";
  else if (/\bP\.?\s?O\.?\s?BOX\b/i.test(form.street))
    errors.street = "PO Boxes are not accepted — a physical street address is required";
  if (!form.city?.trim()) errors.city = "City is required";
  if (!form.province) errors.province = "Select a Canadian province or territory";
  const postal = (form.postal_code || "").toUpperCase().replace(/[\s-]/g, "");
  if (!postal) errors.postal_code = "Postal code is required";
  else if (/^\d{5}(-?\d{4})?$/.test(postal))
    errors.postal_code = "This looks like a US ZIP code — Canadian postal codes use the format A1A 1A1";
  else if (!/^[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTVWXYZ]\d[ABCEGHJ-NPRSTVWXYZ]\d$/.test(postal))
    errors.postal_code = "Enter a valid Canadian postal code (format A1A 1A1)";
  return errors;
};

const EMPTY_FORM = { legal_business_name: "", street: "", city: "", province: "", postal_code: "", business_registration_number: "" };

const DOC_KINDS = [
  { value: "business_registration", label: "Business registration document", hint: "Articles of incorporation, business licence, or CRA registration" },
  { value: "proof_of_address", label: "Proof of business address", hint: "Utility bill or lease dated within the last 3 months, matching your address" },
];

export const CaComplianceStep = ({ API, getAuthConfig, caProfile, onSaved, onBack, isDark }) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [docKind, setDocKind] = useState("business_registration");
  const [uploading, setUploading] = useState(false);
  const [uploadedDoc, setUploadedDoc] = useState(null);
  const fileInputRef = useRef(null);
  const touchedRef = useRef(false);

  useEffect(() => {
    if (caProfile && !touchedRef.current) {
      setForm({
        legal_business_name: caProfile.legal_business_name || "",
        street: caProfile.street || "",
        city: caProfile.city || "",
        province: caProfile.province || "",
        postal_code: caProfile.postal_code || "",
        business_registration_number: caProfile.business_registration_number || "",
      });
    }
    if (caProfile?.supporting_doc_sid) {
      setUploadedDoc({ filename: caProfile.supporting_doc_filename, kind: caProfile.supporting_doc_kind });
      if (caProfile.supporting_doc_kind) setDocKind(caProfile.supporting_doc_kind);
    }
  }, [caProfile]);

  const setField = (key, value) => {
    touchedRef.current = true;
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((e) => ({ ...e, [key]: undefined }));
  };

  const handleContinue = async () => {
    const clientErrors = validateCaForm(form);
    if (Object.keys(clientErrors).length > 0) {
      setErrors(clientErrors);
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/settings/ca-compliance`, form, getAuthConfig());
      toast.success("Canadian regulatory info saved");
      onSaved(res.data.profile);
    } catch (error) {
      const data = error.response?.data || {};
      if (data.errors) setErrors(data.errors);
      toast.error(data.detail || "Could not validate your Canadian regulatory info", { duration: 8000 });
    } finally {
      setSaving(false);
    }
  };

  const handleUploadDocument = async (file) => {
    if (!file) return;
    if (!["application/pdf", "image/jpeg", "image/png"].includes(file.type)) {
      toast.error("Only PDF, JPG or PNG files are accepted by Twilio");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File too large — Twilio accepts documents up to 5 MB");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("document_kind", docKind);
      if (form.business_registration_number?.trim()) fd.append("document_number", form.business_registration_number.trim());
      const res = await axios.post(`${API}/settings/ca-compliance/document`, fd, getAuthConfig());
      setUploadedDoc({ filename: res.data.filename, kind: res.data.document_kind });
      toast.success("Document uploaded — it will be included in your Twilio regulatory bundle");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Document upload failed", { duration: 9000 });
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const fieldError = (key) =>
    errors[key] ? (
      <p className="text-[11px] text-red-400 mt-0.5" data-testid={`ca-field-error-${key}`}>{errors[key]}</p>
    ) : null;

  return (
    <div className="space-y-4" data-testid="ca-compliance-step">
      <div className={`flex items-start gap-2.5 p-3 rounded-lg text-xs ${isDark ? "bg-blue-500/10 text-blue-300 border border-blue-500/20" : "bg-blue-50 text-blue-700 border border-blue-200"}`}>
        <ShieldCheck className="w-4 h-4 mt-0.5 shrink-0" />
        <span>
          Canadian telecom regulations require your <strong>legal business name</strong>, a <strong>valid Canadian address</strong>, and an <strong>official supporting document</strong> (business registration or recent proof of address) before a Canadian number can be provisioned.
          Twilio's reviewers reject bundles submitted without a document.
        </span>
      </div>

      {caProfile?.bundle_status === "pending-review" && (
        <div className={`flex items-start gap-2 p-2.5 rounded-lg text-xs ${isDark ? "bg-amber-500/10 text-amber-300 border border-amber-500/20" : "bg-amber-50 text-amber-700 border border-amber-200"}`} data-testid="ca-bundle-pending-banner">
          <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span>Your Canadian regulatory bundle has been submitted to Twilio and is <strong>awaiting approval</strong> (usually 1-3 business days). You can complete the purchase once it's approved.</span>
        </div>
      )}

      {caProfile?.twilio_address_sid && (
        <div className={`flex items-center gap-2 p-2.5 rounded-lg text-xs ${isDark ? "bg-emerald-500/10 text-emerald-300 border border-emerald-500/20" : "bg-emerald-50 text-emerald-700 border border-emerald-200"}`} data-testid="ca-saved-profile-hint">
          <Info className="w-3.5 h-3.5 shrink-0" />
          <span>Saved regulatory info loaded — review it and continue, or update any field.</span>
        </div>
      )}

      <div className="space-y-3">
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Legal Business Name *</Label>
          <Input value={form.legal_business_name} onChange={(e) => setField("legal_business_name", e.target.value)} className="input-glass h-9" placeholder="e.g., Maple Locksmith Services Inc." data-testid="ca-legal-name-input" />
          {fieldError("legal_business_name")}
          <p className="text-[10px] text-muted-foreground/60">Exactly as registered with Canadian authorities</p>
        </div>

        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Street Address *</Label>
          <Input value={form.street} onChange={(e) => setField("street", e.target.value)} className="input-glass h-9" placeholder="e.g., 290 Bremner Blvd" data-testid="ca-street-input" />
          {fieldError("street")}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">City *</Label>
            <Input value={form.city} onChange={(e) => setField("city", e.target.value)} className="input-glass h-9" placeholder="e.g., Toronto" data-testid="ca-city-input" />
            {fieldError("city")}
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Province / Territory *</Label>
            <Select value={form.province} onValueChange={(v) => setField("province", v)}>
              <SelectTrigger className="input-glass h-9" data-testid="ca-province-select"><SelectValue placeholder="Select..." /></SelectTrigger>
              <SelectContent>
                {CA_PROVINCES.map((p) => (
                  <SelectItem key={p.code} value={p.code} data-testid={`ca-province-${p.code}`}>{p.name} ({p.code})</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {fieldError("province")}
          </div>
        </div>

        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Postal Code *</Label>
          <Input value={form.postal_code} onChange={(e) => setField("postal_code", formatCaPostal(e.target.value))} className="input-glass h-9 font-mono tracking-wider uppercase" placeholder="A1A 1A1" maxLength={7} data-testid="ca-postal-input" />
          {fieldError("postal_code")}
        </div>

        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Business / Corporation Number (recommended)</Label>
          <Input value={form.business_registration_number} onChange={(e) => setField("business_registration_number", e.target.value)} className="input-glass h-9 font-mono" placeholder="e.g., 123456789 or 123456789RC0001" maxLength={20} data-testid="ca-registration-number-input" />
          {fieldError("business_registration_number")}
          <p className="text-[10px] text-muted-foreground/60">Your CRA business number or provincial corporation number — helps Twilio approve faster</p>
        </div>

        {/* Supporting document — required by Twilio's reviewers */}
        <div className={`space-y-2 p-3 rounded-lg border ${isDark ? "border-white/10 bg-white/[0.02]" : "border-gray-200 bg-gray-50"}`} data-testid="ca-document-section">
          <Label className="text-xs font-semibold text-foreground">Supporting Document *</Label>
          <Select value={docKind} onValueChange={setDocKind}>
            <SelectTrigger className="input-glass h-9" data-testid="ca-doc-kind-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {DOC_KINDS.map((k) => (
                <SelectItem key={k.value} value={k.value} data-testid={`ca-doc-kind-${k.value}`}>{k.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-[10px] text-muted-foreground/70">{DOC_KINDS.find((k) => k.value === docKind)?.hint}. The name and address on the document must match the info above. PDF, JPG or PNG, max 5 MB.</p>
          <input ref={fileInputRef} type="file" accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png" className="hidden" onChange={(e) => handleUploadDocument(e.target.files?.[0])} data-testid="ca-doc-file-input" />
          {uploadedDoc ? (
            <div className={`flex items-center justify-between gap-2 p-2 rounded-md text-xs ${isDark ? "bg-emerald-500/10 text-emerald-300" : "bg-emerald-50 text-emerald-700"}`} data-testid="ca-doc-uploaded">
              <span className="flex items-center gap-1.5 min-w-0">
                <CheckCircle2 className="w-3.5 h-3.5 shrink-0" />
                <span className="truncate">{uploadedDoc.filename || "Document"} uploaded</span>
              </span>
              <Button variant="outline" size="sm" className="h-7 text-[11px] shrink-0" disabled={uploading} onClick={() => fileInputRef.current?.click()} data-testid="ca-doc-replace-btn">
                {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : "Replace"}
              </Button>
            </div>
          ) : (
            <Button variant="outline" size="sm" className="w-full h-9" disabled={uploading} onClick={() => fileInputRef.current?.click()} data-testid="ca-doc-upload-btn">
              {uploading ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Uploading to Twilio...</> : <><Upload className="w-3.5 h-3.5 mr-1.5" /> Upload Document</>}
            </Button>
          )}
          {!uploadedDoc && (
            <p className={`text-[10px] ${isDark ? "text-amber-300" : "text-amber-600"}`} data-testid="ca-doc-required-note">
              Twilio rejects Canadian number requests submitted without an official document.
            </p>
          )}
        </div>
      </div>

      <div className="flex gap-2 pt-1">
        <Button variant="ghost" onClick={onBack} className="text-muted-foreground" data-testid="ca-compliance-back-btn">Back</Button>
        <Button onClick={handleContinue} disabled={saving} className="flex-1 btn-primary h-10" data-testid="ca-compliance-continue-btn">
          {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Validating with Twilio...</> : <>Validate & Continue <ArrowRight className="w-4 h-4 ml-2" /></>}
        </Button>
      </div>
    </div>
  );
};

const BUNDLE_STATUS_META = {
  "twilio-approved": {
    label: "Approved",
    icon: CheckCircle2,
    badge: "bg-emerald-500/15 text-emerald-500 border-emerald-500/30",
    desc: "Your Canadian regulatory bundle is approved — you can purchase Canadian numbers.",
  },
  "pending-review": {
    label: "Awaiting Twilio approval",
    icon: Clock,
    badge: "bg-amber-500/15 text-amber-500 border-amber-500/30",
    desc: "Twilio is reviewing your Canadian regulatory bundle. Approval usually takes 1-3 business days.",
  },
  "in-review": {
    label: "In review",
    icon: Clock,
    badge: "bg-amber-500/15 text-amber-500 border-amber-500/30",
    desc: "Twilio is actively reviewing your Canadian regulatory bundle.",
  },
  "twilio-rejected": {
    label: "Rejected",
    icon: XCircle,
    badge: "bg-red-500/15 text-red-500 border-red-500/30",
    desc: "Twilio rejected your bundle — usually because of the supporting document. Upload a clear business registration or recent utility bill (matching your business name and address) in the Canadian Regulatory Info step, then retry the purchase. A fresh bundle will be submitted automatically.",
  },
  draft: {
    label: "Not submitted",
    icon: Info,
    badge: "bg-muted text-muted-foreground border-border",
    desc: "Your bundle hasn't been submitted to Twilio yet. Retry your Canadian purchase to resubmit.",
  },
};

export const CaBundleStatusCard = ({ API, getAuthConfig, isDark }) => {
  const [data, setData] = useState(null);
  const [checking, setChecking] = useState(false);

  const fetchStatus = async (manual = false) => {
    setChecking(true);
    try {
      const res = await axios.get(`${API}/settings/ca-compliance/bundle-status`, getAuthConfig());
      setData(res.data);
      if (manual) {
        if (res.data?.bundle_status === "twilio-approved") toast.success("Your Canadian regulatory bundle is approved!");
        else toast.info(`Bundle status: ${res.data?.bundle_status || "unknown"}`);
      }
    } catch {
      if (manual) toast.error("Could not check bundle status");
    } finally {
      setChecking(false);
    }
  };

  useEffect(() => { fetchStatus(); }, []);

  if (!data?.has_bundle) return null;

  const notRequired = data.bundle_required === false;
  const meta = notRequired ? {
    label: "Not required",
    icon: CheckCircle2,
    badge: "bg-emerald-500/15 text-emerald-500 border-emerald-500/30",
    desc: "Twilio confirmed this Canadian number type doesn't require a regulatory bundle — purchases go through without it. Any previously submitted bundle can be safely ignored.",
  } : (BUNDLE_STATUS_META[data.bundle_status] || BUNDLE_STATUS_META.draft);
  const StatusIcon = meta.icon;

  return (
    <Card className="glass-card border-theme mt-4" data-testid="ca-bundle-status-card">
      <CardContent className="pt-5">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${isDark ? "bg-white/5" : "bg-gray-100"}`}>
            <ShieldCheck className="w-5 h-5 text-[#FFCC00]" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-sm font-semibold text-foreground">Canadian Regulatory Bundle</p>
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-medium ${meta.badge}`} data-testid="ca-bundle-status-badge">
                <StatusIcon className="w-3 h-3" /> {meta.label}
              </span>
            </div>
            <p className="text-xs text-muted-foreground mt-0.5">
              {!notRequired && data.bundle_status === "twilio-rejected" && data.failure_reason ? `Twilio's reason: ${data.failure_reason}` : meta.desc}
              {data.legal_business_name ? ` · ${data.legal_business_name}` : ""}
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={() => fetchStatus(true)} disabled={checking} className="shrink-0" data-testid="ca-bundle-refresh-btn">
            {checking ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />} Check status
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
